import { useEffect, useRef, useState } from "react";
import { ForgotPasswordLink } from "../../components/ForgotPasswordLink";
import { api, errText, media } from "../../lib/api";
import { MapPin, Navigation, Phone, MessageCircle, LogOut, Camera, Loader2, CheckCircle2, Truck, Bell, BellRing, Store, PackageCheck } from "lucide-react";
import { initPwa, enablePush, currentPushState } from "../../lib/push";

const dirTo = (loc, address) =>
  loc
    ? `https://www.google.com/maps/dir/?api=1&destination=${loc.lat},${loc.lng}`
    : `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address || "")}`;

const dirUrl = (o) => dirTo(o.delivery_location, o.address);

const haversine = (a, b) => {
  const R = 6371, dLat = (b.lat - a.lat) * Math.PI / 180, dLng = (b.lng - a.lng) * Math.PI / 180;
  const x = Math.sin(dLat / 2) ** 2 + Math.cos(a.lat * Math.PI / 180) * Math.cos(b.lat * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.asin(Math.sqrt(x));
};

// Greedy nearest-neighbour order from the driver's current position.
const optimizeRoute = (stops, from) => {
  const withPin = stops.filter((s) => s.delivery_location);
  const noPin = stops.filter((s) => !s.delivery_location);
  if (!from || withPin.length < 2) return [...withPin, ...noPin];
  const remaining = [...withPin];
  const ordered = [];
  let cur = from;
  while (remaining.length) {
    let bi = 0, bd = Infinity;
    const currentPosition = cur;
    remaining.forEach((s, i) => { const d = haversine(currentPosition, s.delivery_location); if (d < bd) { bd = d; bi = i; } });
    cur = remaining[bi].delivery_location;
    ordered.push(remaining.splice(bi, 1)[0]);
  }
  return [...ordered, ...noPin];
};

function Login({ onIn }) {
  const [creds, setCreds] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/auth/login", creds);
      if (data.mfa_required) { setError("MFA not supported for drivers"); setBusy(false); return; }
      localStorage.setItem("fl_token", data.token);
      onIn();
    } catch (e) { setError(errText(e)); setBusy(false); }
  };
  return (
    <div className="min-h-screen bg-ink flex items-center justify-center p-6" data-testid="driver-login">
      <form onSubmit={submit} className="w-full max-w-sm bg-plaster p-8 space-y-4 rounded-sm">
        <div className="flex items-center gap-2 text-ink"><Truck size={22} className="text-brass" /><h1 className="font-display text-2xl">Espace livreur</h1></div>
        <input data-testid="driver-email" className="field" placeholder="E-mail" value={creds.email} onChange={(e) => setCreds({ ...creds, email: e.target.value })} />
        <input data-testid="driver-password" type="password" className="field" placeholder="Mot de passe" value={creds.password} onChange={(e) => setCreds({ ...creds, password: e.target.value })} />
        {error ? <p className="text-danger text-sm" data-testid="driver-login-error">{error}</p> : null}
        <button data-testid="driver-login-button" disabled={busy} className="btn-primary w-full">{busy ? "…" : "Se connecter"}</button>
        <a href="/fr/driver/apply" data-testid="driver-signup-link" className="btn-ghost w-full block text-center">Devenir livreur Fleurinelle</a>
        <ForgotPasswordLink locale="fr" email={creds.email} testid="driver-forgot" />
      </form>
    </div>
  );
}

const PickupLeg = ({ o, onPickup, busy }) => (
  <div className="rounded-sm bg-plaster-deep border border-brass-light p-3 space-y-2" data-testid={`pickup-leg-${o.order_number}`}>
    <p className="smallcaps text-[11px] text-brass">Étape 1 · Retrait boutique</p>
    <p className="text-sm text-ink font-medium" data-testid={`pickup-shop-${o.order_number}`}>{o.pickup?.shop_name || "Atelier Fleurinelle"}</p>
    <p className="text-sm text-ink flex items-start gap-1.5"><Store size={14} className="text-brass mt-0.5 flex-shrink-0" /> {o.pickup?.address || "Adresse communiquée par l'atelier"}</p>
    <div className="flex flex-wrap gap-2">
      <a href={dirTo(o.pickup?.location, o.pickup?.address)} target="_blank" rel="noreferrer"
         data-testid={`pickup-navigate-${o.order_number}`} className="btn-secondary text-xs py-2 flex items-center gap-1.5">
        <Navigation size={14} /> Itinéraire vers la boutique
      </a>
      {o.pickup?.phone ? (
        <a href={`tel:${o.pickup.phone}`} data-testid={`pickup-call-${o.order_number}`} className="btn-secondary text-xs py-2 flex items-center gap-1.5">
          <Phone size={14} /> Appeler la boutique
        </a>
      ) : null}
      <button data-testid={`pickup-confirm-${o.order_number}`} disabled={busy} className="btn-primary text-xs py-2 flex items-center gap-1.5" onClick={() => onPickup(o)}>
        {busy ? <Loader2 size={14} className="animate-spin" /> : <PackageCheck size={14} />} Confirmer le retrait
      </button>
    </div>
  </div>
);

function StopCard({ o, seq, showClaim, onClaim, onStart, onDeliver, onPickup, uploading, picking }) {
  const fileRef = useRef(null);
  const needsPickup = !showClaim && o.status === "ready" && !o.picked_up_at;
  return (
    <article className="card p-4 space-y-3" data-testid={`driver-stop-${o.order_number}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-start gap-2">
          {seq ? <span className="w-6 h-6 rounded-full bg-ink text-plaster text-xs flex items-center justify-center flex-shrink-0 mt-0.5" data-testid={`stop-seq-${o.order_number}`}>{seq}</span> : null}
          <div>
            <p className="text-sm text-ink font-medium">{o.recipient_name}</p>
            <p className="text-xs text-muted">{o.order_number} · {o.delivery_window_label}</p>
          </div>
        </div>
        {typeof o.wage_mad === "number" ? <span className="text-sm tabular text-ink">{o.wage_mad} MAD</span> : null}
      </div>
      {showClaim && o.pickup?.address ? (
        <p className="text-xs text-muted flex items-start gap-1.5" data-testid={`pickup-hint-${o.order_number}`}>
          <Store size={12} className="text-brass mt-0.5 flex-shrink-0" /> Retrait : {o.pickup.shop_name} — {o.pickup.address}
        </p>
      ) : null}
      {needsPickup ? <PickupLeg o={o} onPickup={onPickup} busy={picking === o.id} /> : null}
      {!showClaim && o.picked_up_at ? (
        <p className="text-[11px] text-brass flex items-center gap-1" data-testid={`pickup-done-${o.order_number}`}>
          <PackageCheck size={12} /> Retrait confirmé{o.pickup?.shop_name ? ` · ${o.pickup.shop_name}` : ""}
        </p>
      ) : null}
      {!showClaim ? <p className="smallcaps text-[11px] text-muted">Étape 2 · Livraison</p> : null}
      <p className="text-sm text-ink flex items-start gap-1.5"><MapPin size={14} className="text-brass mt-0.5 flex-shrink-0" /> {o.address}</p>
      {o.landmark_note ? <p className="text-xs text-muted ps-5">Repère : {o.landmark_note}</p> : null}
      {o.delivery_location ? <p className="text-xs text-ink ps-5 flex items-center gap-1"><CheckCircle2 size={12} className="text-brass" /> Localisation exacte</p> : null}
      {!showClaim && o.delivery_map_url ? (
        <a href={dirUrl(o)} target="_blank" rel="noreferrer" data-testid={`delivery-map-${o.order_number}`} className="block ps-5">
          <img src={o.delivery_map_url} alt="Carte de livraison" loading="lazy"
               className="w-full h-28 object-cover rounded-sm border border-brass-light" />
        </a>
      ) : null}
      <div className="flex flex-wrap gap-2">
        {showClaim ? (
          <button data-testid={`driver-claim-${o.order_number}`} className="btn-primary text-xs py-2" onClick={() => onClaim(o)}>Prendre cette livraison</button>
        ) : (
          <>
            <a href={dirUrl(o)} target="_blank" rel="noreferrer" data-testid={`driver-navigate-${o.order_number}`} className="btn-secondary text-xs py-2 flex items-center gap-1.5"><Navigation size={14} /> Naviguer</a>
            {o.call_before_delivery && o.recipient_phone ? <a href={`tel:${o.recipient_phone}`} className="btn-secondary text-xs py-2 flex items-center gap-1.5"><Phone size={14} /> Appeler</a> : null}
            {o.recipient_whatsapp ? <a href={`https://wa.me/${o.recipient_whatsapp.replace(/[^\d]/g, "")}`} target="_blank" rel="noreferrer" className="btn-secondary text-xs py-2 flex items-center gap-1.5"><MessageCircle size={14} /> WhatsApp</a> : null}
            {o.status === "ready" ? (
              <button data-testid={`driver-start-${o.order_number}`} disabled={needsPickup} title={needsPickup ? "Confirmez d'abord le retrait" : ""}
                      className="btn-primary text-xs py-2 disabled:opacity-40" onClick={() => onStart(o)}>Démarrer la livraison</button>
            ) : null}
            {o.status === "out_for_delivery" ? (
              <>
                <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden" data-testid={`driver-proof-input-${o.order_number}`} onChange={(e) => e.target.files[0] && onDeliver(o, e.target.files[0])} />
                <button data-testid={`driver-deliver-${o.order_number}`} disabled={uploading === o.id} className="btn-primary text-xs py-2 flex items-center gap-1.5" onClick={() => fileRef.current?.click()}>
                  {uploading === o.id ? <Loader2 size={14} className="animate-spin" /> : <Camera size={14} />} Photo & livrer
                </button>
              </>
            ) : null}
          </>
        )}
      </div>
    </article>
  );
}

export default function DriverApp() {
  const [screen, setScreen] = useState("loading"); // loading | login | signup | dash
  const [me, setMe] = useState(null);
  const [orders, setOrders] = useState({ available: [], mine: [] });
  const [tab, setTab] = useState("available");
  const [uploading, setUploading] = useState(null);
  const [picking, setPicking] = useState(null);
  const [msg, setMsg] = useState("");
  const [pendingTerms, setPendingTerms] = useState(null); // {documents:[{slug,title,body,version}]}
  const [myPos, setMyPos] = useState(null);
  const [push, setPush] = useState("off");
  useEffect(() => { initPwa("driver"); currentPushState().then(setPush); }, []);
  const togglePush = async () => { const ok = await enablePush(); setPush(ok ? "on" : "denied"); };

  const load = () => {
    api.get("/driver/me").then((r) => {
      setMe(r.data); setScreen("dash");
      api.get("/driver/orders").then((o) => setOrders(o.data)).catch(() => {});
    }).catch(() => { setScreen(localStorage.getItem("fl_token") ? "login" : "login"); });
  };
  useEffect(() => { if (localStorage.getItem("fl_token")) load(); else setScreen("login"); }, []);
  useEffect(() => { if (screen !== "dash") return; const id = setInterval(() => api.get("/driver/orders").then((o) => setOrders(o.data)).catch(() => {}), 20000); return () => clearInterval(id); }, [screen]);

  // Track the driver's position; broadcast it for any active (out_for_delivery) deliveries.
  useEffect(() => {
    if (screen !== "dash" || !navigator.geolocation) return;
    const wid = navigator.geolocation.watchPosition(
      (p) => setMyPos({ lat: p.coords.latitude, lng: p.coords.longitude }),
      () => {}, { enableHighAccuracy: true, maximumAge: 10000 });
    return () => navigator.geolocation.clearWatch(wid);
  }, [screen]);
  useEffect(() => {
    if (screen !== "dash" || !myPos) return;
    const push = () => orders.mine.filter((o) => o.status === "out_for_delivery")
      .forEach((o) => api.post(`/driver/orders/${o.id}/location`, myPos).catch(() => {}));
    push();
    const id = setInterval(push, 15000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myPos, orders.mine, screen]);

  const refresh = () => { api.get("/driver/me").then((r) => setMe(r.data)); api.get("/driver/orders").then((o) => setOrders(o.data)); };
  const claim = async (o) => {
    try { await api.post(`/driver/orders/${o.id}/claim`); refresh(); }
    catch (e) {
      // Backend returns 428 with {code:"terms_reaccept_required", pending:[...]} when the
      // Delivery Partner Agreement has a new version — pop the modal so the driver can accept.
      const st = e?.response?.status;
      const det = e?.response?.data?.detail;
      if (st === 428 && det && det.code === "terms_reaccept_required") {
        try {
          const { data } = await api.get("/legal/driver/pending", { params: { locale: (me?.locale || "fr") } });
          setPendingTerms({ documents: data.documents || [] });
        } catch { setPendingTerms({ documents: [] }); }
      } else { setMsg(errText(e)); }
    }
  };
  const acceptTerms = async () => {
    try {
      await api.post("/legal/accept", { slugs: (pendingTerms?.documents || []).map((d) => d.slug),
                                        context: "driver_login_reaccept" });
      setPendingTerms(null);
      setMsg("");
    } catch (e) { setMsg(errText(e)); }
  };
  const pickup = async (o) => {
    setPicking(o.id); setMsg("");
    try { await api.post(`/driver/orders/${o.id}/pickup`); refresh(); }
    catch (e) { setMsg(errText(e)); } finally { setPicking(null); }
  };
  const start = async (o) => { try { await api.post(`/driver/orders/${o.id}/status`, { status: "out_for_delivery" }); refresh(); } catch (e) { setMsg(errText(e)); } };
  const deliver = async (o, file) => {
    setUploading(o.id); setMsg("");
    try { const fd = new FormData(); fd.append("photo", file); await api.post(`/driver/orders/${o.id}/proof`, fd, { headers: { "Content-Type": "multipart/form-data" } }); refresh(); }
    catch (e) { setMsg(errText(e)); } finally { setUploading(null); }
  };
  const logout = () => { localStorage.removeItem("fl_token"); setScreen("login"); setMe(null); };
  const requestPayout = async () => { try { await api.post("/driver/payout-request"); setMsg(""); refresh(); } catch (e) { setMsg(errText(e)); } };

  if (screen === "loading") return <div className="min-h-screen bg-ink flex items-center justify-center"><Loader2 className="animate-spin text-brass" /></div>;
  if (screen === "login") return <Login onIn={load} />;

  const list = tab === "available" ? orders.available : optimizeRoute(orders.mine, myPos);
  return (
    <div className="min-h-screen bg-plaster" data-testid="driver-dashboard">
      <header className="bg-ink text-plaster px-4 py-3 flex items-center justify-between sticky top-0 z-10">
        <div className="flex items-center gap-2"><Truck size={18} className="text-brass" />
          <div><p className="text-sm">{me?.name}</p><p className="text-[11px] text-plaster/70">{me?.city_name} · {me?.driver_type === "network" ? "Réseau Fleurinelle" : "Livreur boutique"}</p></div>
        </div>
        <div className="flex items-center gap-3">
          {me?.driver_type === "network" ? <span className="text-xs text-brass" data-testid="driver-earnings">{me?.earnings_mad} MAD</span> : null}
          <button data-testid="driver-push-toggle" onClick={togglePush} title="Notifications">
            {push === "on" ? <BellRing size={16} className="text-brass" /> : <Bell size={16} />}
          </button>
          <button data-testid="driver-logout" onClick={logout}><LogOut size={16} /></button>
        </div>
      </header>
      <div className="flex border-b border-brass-light bg-plaster-deep">
        <button data-testid="driver-tab-available" onClick={() => setTab("available")} className={`px-4 py-3 text-sm ${tab === "available" ? "text-ink font-bold border-b-2 border-brass" : "text-muted"}`}>À prendre ({orders.available.length})</button>
        <button data-testid="driver-tab-mine" onClick={() => setTab("mine")} className={`px-4 py-3 text-sm ${tab === "mine" ? "text-ink font-bold border-b-2 border-brass" : "text-muted"}`}>Mes livraisons ({orders.mine.length})</button>
      </div>
      <main className="p-4 max-w-2xl mx-auto space-y-3">
        {me?.driver_type === "network" && me?.earnings ? (
          <div className="card p-4 space-y-2" data-testid="driver-earnings-panel">
            <div className="flex items-center justify-between">
              <div>
                <p className="smallcaps text-xs">Gains</p>
                <p className="text-lg text-ink tabular"><b>{me.earnings.available}</b> MAD <span className="text-xs text-muted">disponibles</span></p>
                <p className="text-[11px] text-muted">En attente : {me.earnings.requested} · Versés : {me.earnings.paid}</p>
              </div>
              <button data-testid="driver-request-payout" disabled={!me.earnings.available} onClick={requestPayout}
                      className="btn-primary text-xs disabled:opacity-40">Demander un versement</button>
            </div>
          </div>
        ) : null}
        {msg ? <p className="text-danger text-sm" data-testid="driver-msg">{msg}</p> : null}
        {tab === "mine" && orders.mine.filter((o) => o.delivery_location).length > 1 ? (
          <p className="text-xs text-brass flex items-center gap-1" data-testid="route-optimized">Itinéraire optimisé{myPos ? " depuis votre position" : ""}</p>
        ) : null}
        {list.length === 0 ? <p className="text-sm text-muted py-10 text-center" data-testid="driver-empty">Aucune livraison ici pour le moment.</p> : null}
        {list.map((o, i) => (
          <StopCard key={o.id} o={o} seq={tab === "mine" ? i + 1 : null} showClaim={tab === "available"} onClaim={claim} onStart={start} onDeliver={deliver} onPickup={pickup} uploading={uploading} picking={picking} />
        ))}
      </main>
      {pendingTerms ? (
        <div className="fixed inset-0 scrim z-50 flex items-center justify-center p-4"
             data-testid="driver-reaccept-modal">
          <div className="bg-plaster max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col border border-brass">
            <div className="p-5 border-b border-brass-light">
              <p className="smallcaps text-brass mb-1">Nouvelle version</p>
              <h2 className="font-display text-xl text-ink">Acceptez la nouvelle version pour continuer</h2>
              <p className="text-xs text-muted mt-1">Vous ne pouvez pas réserver de nouvelle livraison tant que vous n'avez pas accepté la version en vigueur du Contrat de livreur partenaire.</p>
            </div>
            <div className="p-5 overflow-y-auto space-y-6 text-[13px] text-charcoal leading-relaxed">
              {(pendingTerms.documents || []).map((d) => (
                <div key={d.slug}>
                  <p className="smallcaps text-brass">v{d.version} · {d.effective_date}</p>
                  <h3 className="font-display text-lg text-ink mt-1 mb-2">{d.title}</h3>
                  <pre className="whitespace-pre-wrap font-sans text-[12px]" data-testid={`reaccept-body-${d.slug}`}>{d.body}</pre>
                </div>
              ))}
              {(pendingTerms.documents || []).length === 0 ? (
                <p className="text-sm text-muted">Rechargez la page — nous n'avons pas pu récupérer le texte à jour.</p>
              ) : null}
            </div>
            <div className="p-4 border-t border-brass-light flex justify-end gap-2">
              <button className="btn-ghost text-xs" onClick={logout} data-testid="reaccept-decline">
                Refuser et se déconnecter
              </button>
              <button className="btn-primary text-xs" onClick={acceptTerms} data-testid="reaccept-accept">
                J'accepte cette version
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
